import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Dialog from "components/Overlays/ResponsiveDialog";
import { FiArrowLeft, FiChevronRight, FiMenu, FiRefreshCw } from "react-icons/fi";
import useProfile from "api/UserResource/useProfile";
import useLogout from "api/UserResource/useLogout";
import useCheckIfUserIsSubscribed from "api/SubscriptionsResource/useCheckIfUserIsSubscribed";
import useAuthStore from "store/authStore";
import { Role } from "utils/server-constants/roles";
import AddSeatsModal from "components/AddSeatsModal";
import PortalAccessNotice from "components/PortalAccessNotice";
import AccountNavigation from "./AccountNavigation";
import { accountSections } from "./accountSections";
import DeleteAccount from "./DeleteAccount";
import "./account.css";

export const AccountSkeleton = () => (
	<div className="account-skeleton" role="status" aria-label="Loading account information">
		<span className="account-skeleton-line" />
		<span className="account-skeleton-line short" />
		<div className="account-form-grid">
			{[0, 1, 2, 3].map((item) => (
				<span key={item} className="account-skeleton-field" />
			))}
		</div>
		<span className="account-skeleton-line short" />
		<span className="sr-only">Loading account information…</span>
	</div>
);

const AccountLayout = ({ children, isLoading, profile }) => {
	const { pathname } = useLocation();
	const { data: profileResponse, isError, refetch } = useProfile();
	const user = profile || profileResponse?.data;
	const { hasRoles, hasPermissions } = useAuthStore();
	const { mutateAsync: logout, isLoading: isLoggingOut } = useLogout();
	const { data: subscription } = useCheckIfUserIsSubscribed();
	const [menuOpen, setMenuOpen] = useState(false);
	const [deleteOpen, setDeleteOpen] = useState(false);
	const [seatsOpen, setSeatsOpen] = useState(false);
	const menuButton = useRef(null);
	const deleteTrigger = useRef(null);
	const current =
		accountSections.find(
			(item) =>
				item.path === pathname &&
				(!item.role || hasRoles([item.role])) &&
				(!item.permission || hasPermissions([item.permission]))
		) ||
		accountSections.find((item) => item.path === pathname) ||
		accountSections[0];
	const canAddSeats =
		subscription?.data?.category === "institution" && hasRoles([Role.Institution, Role.Educator]);

	useEffect(() => {
		setMenuOpen(false);
	}, [pathname]);
	useEffect(() => {
		const media = window.matchMedia("(min-width: 1024px)");
		const closeOnDesktop = (event) => {
			if (event.matches) setMenuOpen(false);
		};
		media.addListener(closeOnDesktop);
		return () => media.removeListener(closeOnDesktop);
	}, []);

	const closeMenu = () => {
		setMenuOpen(false);
		menuButton.current?.focus();
	};
	const navigationProps = {
		profile: user,
		isLoading,
		isLoggingOut,
		canAddSeats,
		onNavigate: () => setMenuOpen(false),
		onLogout: async () => {
			try {
				await logout();
			} catch (_) {
				/* The mutation displays errors. */
			}
		},
		onAddSeats: () => {
			setMenuOpen(false);
			setSeatsOpen(true);
		},
		onDelete: (event) => {
			deleteTrigger.current = event.currentTarget;
			setMenuOpen(false);
			setDeleteOpen(true);
		},
	};

	return (
		<div className="account-workspace">
			<a className="account-skip-link" href="#account-content">
				Skip to account content
			</a>
			<div className="account-topline">
				<nav aria-label="Breadcrumb" className="account-breadcrumb">
					<Link to="/my-account">My account</Link>
					<FiChevronRight aria-hidden="true" />
					<span aria-current="page">{current.label}</span>
				</nav>
				<Link to="/books" className="account-back-link">
					<FiArrowLeft aria-hidden="true" /> Back to books
				</Link>
			</div>
			<div className="account-columns">
				<aside className="account-desktop-nav">
					<AccountNavigation {...navigationProps} />
				</aside>
				<div className="account-main">
					<button
						ref={menuButton}
						type="button"
						className="account-mobile-menu"
						aria-haspopup="dialog"
						aria-expanded={menuOpen}
						aria-controls="account-menu-dialog"
						onClick={() => setMenuOpen(true)}
					>
						<FiMenu aria-hidden="true" />
						<span>Account menu</span>
						<span className="account-mobile-current">{current.label}</span>
					</button>
					<main id="account-content" tabIndex={-1}>
						<header className="account-page-heading">
							<span className="account-eyebrow">{current.group}</span>
							<h1>{current.title}</h1>
							<p>{current.description}</p>
						</header>
						<div className="account-content" key={pathname}>
							<PortalAccessNotice />
							{isError ? (
								<div className="account-empty" role="alert">
									<FiRefreshCw aria-hidden="true" />
									<h2>We couldn’t load your account</h2>
									<p>Please try again to see your details.</p>
									<button type="button" className="account-button" onClick={() => refetch()}>
										Try again
									</button>
								</div>
							) : isLoading ? (
								<AccountSkeleton />
							) : (
								children
							)}
						</div>
					</main>
					<p className="account-support">
						Need a hand?{" "}
						<Link to="/support">
							Visit support <FiChevronRight aria-hidden="true" />
						</Link>
					</p>
				</div>
			</div>
			<Dialog
				id="account-menu-dialog"
				header="Your account"
				visible={menuOpen}
				onHide={closeMenu}
				className="account-dialog account-menu-dialog"
				maskClassName="account-dialog-mask"
				position="left"
				draggable={false}
				resizable={false}
				blockScroll
				dismissableMask
				ariaCloseIconLabel="Close account menu"
			>
				<AccountNavigation {...navigationProps} />
			</Dialog>
			<DeleteAccount
				visible={deleteOpen}
				onHide={() => {
					setDeleteOpen(false);
					if (deleteTrigger.current?.isConnected) deleteTrigger.current.focus();
					else menuButton.current?.focus();
				}}
			/>
			{canAddSeats && (
				<AddSeatsModal
					visible={seatsOpen}
					onHide={() => setSeatsOpen(false)}
					subscription={subscription.data}
				/>
			)}
		</div>
	);
};

export default AccountLayout;
